import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

// Use unified date utils (configured timezone)
import { moment } from '../utils/dateUtils';

import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';

import { Spinner, BrowserTitle, DateSwitcher } from '../components';
import PaymentModal from '../components/Proposal/PaymentModal';

import paymentServices from '../services/paymentServices';
import printServices from '../services/printServices';
import proposalsServices from '../services/proposalsServices';

const STATUS_CHIP_COLOR = {
  completed: 'success',
  submitted: 'warning',
  pending: 'info',
  failed: 'error',
  reversed: 'default',
  refunded: 'default',
};

const fmtMoney = (v) => {
  const n = Number(v) || 0;
  const abs = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${n < 0 ? '-' : ''}$${abs}`;
};

const PaymentsJournal = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialDate = () => {
    const urlDate = searchParams.get('date');
    if (urlDate && moment(urlDate).isValid()) return moment(urlDate).format();
    const saved = sessionStorage.getItem('payments_journal_date');
    if (saved && moment(saved).isValid()) return moment(saved).format();
    return moment().format();
  };

  const [date, setDateState] = useState(getInitialDate);
  const [tab, setTab] = useState('active');
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [expected, setExpected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);

  // Print preview modal (click on PO)
  const [printProposalId, setPrintProposalId] = useState(null);
  // Payment Information modal (click on amount)
  const [paymentTarget, setPaymentTarget] = useState(null); // { proposalId, proposal }
  // Daily report printing
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [serverPrinters, setServerPrinters] = useState([]);
  const [printer, setPrinter] = useState('');
  const [copies, setCopies] = useState(1);
  const [printing, setPrinting] = useState(false);

  const setDate = (d) => {
    setDateState(d);
    sessionStorage.setItem('payments_journal_date', d);
    setSearchParams({ date: moment(d).format('YYYY-MM-DD') }, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const from = moment(date).startOf('day').toISOString();
      const to = moment(date).startOf('day').add(1, 'day').toISOString();
      const dateStr = moment(date).format('YYYY-MM-DD');
      const [journalRes, expectedRes] = await Promise.all([
        paymentServices.getJournal(from, to),
        paymentServices.getJournalExpected(dateStr),
      ]);
      setRows(journalRes.data || []);
      setExpected(expectedRes.data || []);
    } catch (e) {
      console.log('Error loading payments journal:', e);
      toast.error('Failed to load payments journal');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  const matches = useCallback(
    (row) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return [row.publicId, row.clientName, row.methodName, row.expectedMethod, String(row.amount ?? '')]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    },
    [query]
  );

  const activeRows = useMemo(() => rows.filter((r) => !r.checkedAt && matches(r)), [rows, matches]);
  const checkedRows = useMemo(() => rows.filter((r) => r.checkedAt && matches(r)), [rows, matches]);
  const expectedRows = useMemo(() => expected.filter(matches), [expected, matches]);

  const dayTotals = useMemo(() => {
    const completed = rows.filter((r) => r.status === 'completed').reduce((s, r) => s + Number(r.amount || 0), 0);
    const submitted = rows.filter((r) => r.status === 'submitted' || r.status === 'pending').reduce((s, r) => s + Number(r.amount || 0), 0);
    return { completed, submitted };
  }, [rows]);

  const toggleChecked = async (row, checked) => {
    setTogglingId(row.id);
    try {
      await paymentServices.setPaymentChecked(row.id, checked);
      window.dispatchEvent(new Event('payments-unchecked-changed'));
      await load();
    } catch (e) {
      console.log('Error toggling checked:', e);
      toast.error('Failed to update payment');
    } finally {
      setTogglingId(null);
    }
  };

  const openPrintDialog = async () => {
    setPrintDialogOpen(true);
    try {
      const res = await printServices.getServerPrinters();
      const list = res.data?.printers || [];
      setServerPrinters(list);
      setPrinter((prev) => prev || res.data?.defaultPrinter || list[0]?.name || '');
    } catch (e) {
      console.log('Error loading printers:', e);
      toast.error('Failed to load printers');
    }
  };

  const printReport = async () => {
    setPrinting(true);
    try {
      const res = await printServices.printPaymentsReport({
        date: moment(date).format('YYYY-MM-DD'),
        from: moment(date).startOf('day').toISOString(),
        to: moment(date).startOf('day').add(1, 'day').toISOString(),
        printer,
        copies,
      });
      toast.success(`Report sent to ${res.data?.printer}${res.data?.jobId ? ` (job ${res.data.jobId})` : ''}`);
      setPrintDialogOpen(false);
    } catch (e) {
      console.log('Error printing report:', e);
      toast.error(e.response?.data?.message || 'Failed to print report');
    } finally {
      setPrinting(false);
    }
  };

  const openPaymentModal = async (row) => {
    try {
      const res = await proposalsServices.getProposalById(row.proposalId);
      setPaymentTarget({ proposalId: row.proposalId, proposal: res.data });
    } catch (e) {
      console.log('Error loading proposal:', e);
      toast.error('Failed to load proposal');
    }
  };

  const renderJournalTable = (list, isCheckedTab) => (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Time</TableCell>
            <TableCell>PO</TableCell>
            <TableCell>Client</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell>Method</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Recorded by</TableCell>
            <TableCell align="center">{isCheckedTab ? 'Checked' : ''}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                No payments
              </TableCell>
            </TableRow>
          )}
          {list.map((row) => (
            <TableRow key={row.id} hover>
              <TableCell>{moment(row.paidAt).format('h:mm A')}</TableCell>
              <TableCell>
                <Button size="small" onClick={() => setPrintProposalId(row.proposalId)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                  {row.publicId || '—'}
                </Button>
              </TableCell>
              <TableCell>{row.clientName || '—'}</TableCell>
              <TableCell align="right">
                <Button
                  size="small"
                  onClick={() => openPaymentModal(row)}
                  sx={{ textTransform: 'none', fontWeight: 600, color: Number(row.amount) < 0 ? 'error.main' : undefined }}
                >
                  {fmtMoney(row.amount)}
                </Button>
              </TableCell>
              <TableCell>
                {row.methodName || (row.source === 'square' ? 'Square' : '—')}
                {row.isDeposit ? ' (deposit)' : ''}
              </TableCell>
              <TableCell>
                <Chip size="small" label={row.status} color={STATUS_CHIP_COLOR[row.status] || 'default'} variant={row.status === 'completed' ? 'filled' : 'outlined'} />
                {row.source === 'square' && <Chip size="small" label="Square" sx={{ ml: 0.5 }} variant="outlined" />}
              </TableCell>
              <TableCell>{row.recordedByName || (row.source === 'square' ? 'Auto (Square)' : '—')}</TableCell>
              <TableCell align="center">
                {isCheckedTab ? (
                  <Tooltip title={`Checked by ${row.checkedByName || '—'} at ${moment(row.checkedAt).format('MM/DD h:mm A')}`}>
                    <span>
                      <CheckCircleIcon color="success" fontSize="small" sx={{ verticalAlign: 'middle' }} />
                      <IconButton size="small" onClick={() => toggleChecked(row, false)} disabled={togglingId === row.id} title="Move back to Active">
                        <UndoIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => toggleChecked(row, true)}
                    disabled={togglingId === row.id}
                  >
                    Checked
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderExpectedTable = (list) => (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Installed</TableCell>
            <TableCell>PO</TableCell>
            <TableCell>Client</TableCell>
            <TableCell>Expected method</TableCell>
            <TableCell align="right">Contract</TableCell>
            <TableCell align="right">Paid</TableCell>
            <TableCell align="right">Balance</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                No unpaid jobs with an expected payment method on this date
              </TableCell>
            </TableRow>
          )}
          {list.map((row) => (
            <TableRow key={row.proposalId} hover>
              <TableCell>{row.installDate ? moment(row.installDate).format('MM/DD/YYYY') : '—'}</TableCell>
              <TableCell>
                <Button size="small" onClick={() => setPrintProposalId(row.proposalId)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                  {row.publicId || '—'}
                </Button>
              </TableCell>
              <TableCell>{row.clientName || '—'}</TableCell>
              <TableCell>{row.expectedMethod || '—'}</TableCell>
              <TableCell align="right">{fmtMoney(row.contractPrice)}</TableCell>
              <TableCell align="right">{fmtMoney(row.paid)}</TableCell>
              <TableCell align="right">
                <Button
                  size="small"
                  onClick={() => openPaymentModal(row)}
                  sx={{ textTransform: 'none', fontWeight: 600, color: 'error.main' }}
                >
                  {fmtMoney(row.balance)}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <BrowserTitle title="Payments | AirMaxx CRM" />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5">Payments</Typography>
        <DateSwitcher date={date} setDate={setDate} />
        <Tooltip title="Print daily report">
          <IconButton onClick={openPrintDialog}>
            <PrintIcon />
          </IconButton>
        </Tooltip>
        <TextField
          size="small"
          placeholder="Search PO, client, method..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 260 }}
        />
      </Box>

      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
        Completed: <b>{fmtMoney(dayTotals.completed)}</b>
        {dayTotals.submitted !== 0 && (
          <>
            {' '}&nbsp;|&nbsp; Under review: <b>{fmtMoney(dayTotals.submitted)}</b>
          </>
        )}
      </Typography>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mt: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="active" label={`Active (${activeRows.length})`} />
        <Tab value="checked" label={`Checked (${checkedRows.length})`} />
        <Tab value="expected" label={`Expected (${expectedRows.length})`} />
      </Tabs>

      {loading ? (
        <Spinner />
      ) : (
        <>
          {tab === 'active' && renderJournalTable(activeRows, false)}
          {tab === 'checked' && renderJournalTable(checkedRows, true)}
          {tab === 'expected' && renderExpectedTable(expectedRows)}
        </>
      )}

      {/* Proposal print preview (same output as /proposals/print-batch) */}
      <Dialog open={!!printProposalId} onClose={() => setPrintProposalId(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
          Proposal preview
          <IconButton onClick={() => setPrintProposalId(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {printProposalId && (
            <iframe
              title="Proposal preview"
              src={`/proposals/print-batch?ids=${printProposalId}`}
              style={{ width: '100%', height: '80vh', border: 0 }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Daily report printing */}
      <Dialog open={printDialogOpen} onClose={() => !printing && setPrintDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Print daily report — {moment(date).format('MM/DD/YYYY')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="report-printer">Printer</InputLabel>
            <Select
              labelId="report-printer"
              label="Printer"
              value={printer}
              onChange={(e) => setPrinter(e.target.value)}
            >
              {serverPrinters.map((p) => (
                <MenuItem key={p.name} value={p.name}>
                  {p.name}
                  {p.isDefault ? ' (default)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Copies"
            value={copies}
            onChange={(e) => setCopies(Math.min(Math.max(parseInt(e.target.value, 10) || 1, 1), 10))}
            inputProps={{ min: 1, max: 10 }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintDialogOpen(false)} disabled={printing}>
            Cancel
          </Button>
          <Button variant="contained" onClick={printReport} disabled={!printer || printing} startIcon={printing ? <CircularProgress size={16} /> : <PrintIcon />}>
            Print
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Information modal — same component as in the proposal editor */}
      {paymentTarget && (
        <PaymentModal
          open={!!paymentTarget}
          onClose={() => setPaymentTarget(null)}
          proposalId={paymentTarget.proposalId}
          proposal={paymentTarget.proposal}
          refresh={load}
        />
      )}
    </Box>
  );
};

export default PaymentsJournal;
